import { useTheme } from '@core/hooks/useTheme';
import { useUserPreferences } from '@core/hooks/useUserPreferences';
import { getAvatarColor, resolveAvatarThemeKey } from '@core/utils/getInitials';
import { avatarPalettes } from '@soteria/design-system-ve';
import { useMemo } from 'react';

/**
 * Shared hook that resolves the avatar background color based on theme, user preferences,
 * and the user's name/email. Deduplicates logic previously repeated across AccountTab,
 * UserProfileButtonBase, and UserProfileMenu.
 */
export function useAvatarColor(name?: string | null, email?: string | null) {
  const { isDarkTheme, isHighContrast } = useTheme();
  const { avatarColorIndex } = useUserPreferences();

  const themeKey = resolveAvatarThemeKey(isDarkTheme, isHighContrast);
  const palette = avatarPalettes[themeKey];

  const avatarBackgroundColor = useMemo(() => {
    if (avatarColorIndex !== null && avatarColorIndex >= 0 && avatarColorIndex < palette.length) {
      return palette[avatarColorIndex];
    }

    return getAvatarColor(name || email, palette);
  }, [name, email, palette, avatarColorIndex]);

  return { avatarBackgroundColor, palette } as const;
}
